import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';

interface AutoRecordingErrorModalProps {
  open: boolean;
  onClose: () => void;
  onTurnOffAndRetry: () => void;
  loading?: boolean;
}

const AutoRecordingErrorModal: React.FC<AutoRecordingErrorModalProps> = ({ 
  open, 
  onClose,
  onTurnOffAndRetry,
  loading = false
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Auto-Recording Setup Issue</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Looks like you haven't setup auto recording correctly.
          </Typography>
        </Alert>
        
        <Typography variant="body2" paragraph>
          We detected that auto-recording is not configured properly for your Microsoft 365 account. 
          This prevents us from automatically recording Teams meetings.
        </Typography>

        <Typography variant="body2" paragraph>
          You have two options:
        </Typography>

        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <strong>Turn off auto-recording and create meeting:</strong> We'll disable auto-recording 
            and create your meeting without it. You can manually record the meeting if needed.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Cancel and fix setup:</strong> Close this dialog and complete the auto-recording 
            setup in your organization settings before creating the meeting.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onTurnOffAndRetry}
          disabled={loading}
          color="primary"
        >
          {loading ? 'Processing...' : 'Turn Off Auto Recording & Create Meeting'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoRecordingErrorModal;

